// src/purchasing/policy/purchaseOrderRules.ts

/** Canonical order status labels used everywhere in policy. */
export type OrderStatus =
    | "Created"
    | "Changed"
    | "Sent"
    | "Confirmed"
    | "Shipped"
    | "In Progress"
    | "Completed"
    | "Received Short"
    | "Cancelled";

/** Canonical order-row status labels. */
export type RowStatus = "New" | "In Progress" | "Completed" | "Cancelled";

/** Single place to tweak status ids (from your list). */
export const STATUS_LIST: ReadonlyArray<{ value: number; label: OrderStatus }> = [
    { value: 28, label: "Created" },
    { value: 29, label: "Changed" },
    { value: 30, label: "Sent" },
    { value: 31, label: "Confirmed" },
    { value: 32, label: "Shipped" },
    { value: 33, label: "In Progress" },
    { value: 34, label: "Completed" },
    { value: 35, label: "Received Short" },
    { value: 36, label: "Cancelled" },
] as const;

export const STATUS_BY_ID = new Map<number, OrderStatus>(
    STATUS_LIST.map(s => [s.value, s.label])
);

export const STATUS_ID: Readonly<Record<OrderStatus, number>> = Object.freeze(
    STATUS_LIST.reduce((acc, s) => ({ ...acc, [s.label]: s.value }), {} as Record<OrderStatus, number>)
);

/** Convert API numeric status -> canonical label. Defaults to "Created" if unknown. */
export function statusLabelFromId(id?: number | null): OrderStatus {
    return (id != null && STATUS_BY_ID.get(id)) || "Created";
}

/** Actions we may expose/guard. */
export type ActionKey =
    // Order-level transitions
    | "Send"
    | "SetConfirmed"
    | "SetShipped"
    | "SetReceivedShort"
    | "Cancel"
    // Row-level actions
    | "Add_Row"
    | "Delete_Row"
    | "Edit_Row" // general row edit toggle
    | "Edit_Quantity_Row"
    | "Edit_DiscountPct_Row"
    | "Edit_ItemPrice_Row"
    | "Edit_ETA_Row"
    | "Edit_RTA_Row"
    | "Edit_Comment_Row";

/** Fields we gate with `policy.field(...)` (non-rows a.k.a. meta). */
export type MetaFieldKey =
    | "Supplier"
    | "Warehouse"
    | "Message"
    | "CurrencyExchangeRate"
    | "ShippingMethod"
    | "ShippingTerms"
    | "ShippingTermsDestination"
    | "SendType"
    | "SendDestination"
    | "Comment";

/** Field key union consumed by `policy.field(key)`. */
export type FieldKey =
    | `Meta.${MetaFieldKey}`
    | "Row.Quantity"
    | "Row.DiscountPct"
    | "Row.ItemPrice"
    | "Row.ETA"
    | "Row.RTA"
    | "Row.Comment";

/** Feature flags needed to evaluate some rules (from footnotes). */
export interface PolicyEnv {
    /** (1) External warehouse => cannot Add Row while Order is In Progress */
    isExternalWarehouse: boolean;
    /** (2) Cancel row only if receiving not started */
    hasAnyReceivingStartedForRow?: (rowId: number) => boolean;
}

/* ----------------------------- RULE MATRICES ----------------------------- */

/** Which order actions are allowed from each status (Sheet2 rows 10–14). */
export const TRANSITIONS: Readonly<Record<OrderStatus, Readonly<ActionKey[]>>> = {
    Created: ["Send", "Cancel"],
    Changed: ["Send", "Cancel"],
    Sent: ["SetConfirmed", "SetShipped", "Cancel"],
    Confirmed: ["SetShipped", "Cancel"],
    Shipped: [],               // moves to "In Progress" via logistics, no direct actions here
    "In Progress": ["SetReceivedShort"],
    "Received Short": [],
    Completed: [],
    Cancelled: [],
} as const;

/** Meta editability (Sheet2 "Properties" + top grid). */
export const META_EDIT: Readonly<Record<OrderStatus, Partial<Record<MetaFieldKey, boolean>>>> = {
    Created: {
        Supplier: true, Warehouse: true, Message: true, CurrencyExchangeRate: true,
        ShippingMethod: true, ShippingTerms: true, ShippingTermsDestination: true,
        SendType: true, SendDestination: true, Comment: true,
    },
    Changed: {
        Supplier: false, Warehouse: false, Message: true, CurrencyExchangeRate: true,
        ShippingMethod: true, ShippingTerms: true, ShippingTermsDestination: true,
        SendType: true, SendDestination: true, Comment: true,
    },
    Sent: {
        Supplier: false, Warehouse: false, Message: true, CurrencyExchangeRate: true,
        ShippingMethod: true, ShippingTerms: true, ShippingTermsDestination: true,
        SendType: true, SendDestination: true, Comment: true,
    },
    Confirmed: {
        Supplier: false, Warehouse: false, Message: true, CurrencyExchangeRate: true,
        ShippingMethod: true, ShippingTerms: true, ShippingTermsDestination: true,
        SendType: true, SendDestination: true, Comment: true,
    },
    Shipped: {
        Message: false, CurrencyExchangeRate: false,
        ShippingMethod: false, ShippingTerms: false, ShippingTermsDestination: false,
        SendType: false, SendDestination: false, Comment: true,
    },
    "In Progress": { Comment: true },
    "Received Short": { Comment: true },
    Completed: { Comment: false },
    Cancelled: { Comment: false },
} as const;

/**
 * Row-level rules (Sheet2 "Order Status" rows 31–39 + footnotes 1–4).
 * We encode "restricted" as: allowed only when the row itself is New or In Progress.
 */
export const ROW_RULES = {
    Created: {
        add: true, edit: true, cancelRow: true,
        fields: { Quantity: true, DiscountPct: true, ItemPrice: true, ETA: true, RTA: true, Comment: true }
    },
    Changed: {
        add: true, edit: true, cancelRow: true,
        fields: { Quantity: true, DiscountPct: true, ItemPrice: true, ETA: true, RTA: true, Comment: true }
    },
    Sent: {
        add: true, edit: true, cancelRow: true,
        fields: { Quantity: true, DiscountPct: true, ItemPrice: true, ETA: true, RTA: true, Comment: true }
    },
    Confirmed: {
        add: true, edit: true, cancelRow: true,
        fields: { Quantity: true, DiscountPct: true, ItemPrice: true, ETA: true, RTA: true, Comment: true }
    },
    Shipped: {
        add: false, edit: true, cancelRow: true,
        fields: { Quantity: true, DiscountPct: true, ItemPrice: true, ETA: true, RTA: true, Comment: true }
    },
    "In Progress": {
        add: false, edit: "restricted", cancelRow: "ifReceivingNotStarted",
        fields: { Quantity: "restricted", DiscountPct: "restricted", ItemPrice: "restricted", ETA: "restricted", RTA: false, Comment: "restricted" }
    },
    Completed: {
        add: false, edit: false, cancelRow: false,
        fields: { Quantity: false, DiscountPct: false, ItemPrice: false, ETA: false, RTA: false, Comment: false }
    },
    "Received Short": {
        add: false, edit: "restricted", cancelRow: false,
        fields: { Quantity: false, DiscountPct: false, ItemPrice: false, ETA: false, RTA: false, Comment: true }
    },
    Cancelled: {
        add: false, edit: false, cancelRow: false,
        fields: { Quantity: false, DiscountPct: false, ItemPrice: false, ETA: false, RTA: false, Comment: false }
    },
} as const;




type RowLike = {
    PurchaseOrderRowId?: number;
    RowStatus_Value?: RowStatus | string;
    Status_Value?: RowStatus | string;
    Status?: RowStatus | string;
    QuantityReceived?: number | null;
};

export interface PolicyInit {
    /** Numeric status id from form values (e.g. 28..36). */
    statusId?: number | null;
    /** All current rows (used for per-row checks). */
    rows?: RowLike[];
    /** Environment flags to evaluate special conditions. */
    env: PolicyEnv;
}

/** Normalize any row to a RowStatus string. */
function getRowStatus(row?: RowLike): RowStatus {
    const raw =
        (row?.RowStatus_Value ?? row?.Status_Value ?? row?.Status ?? "New") as string;
    const s = raw.toString().trim();
    if (s === "New" || s === "In Progress" || s === "Completed" || s === "Cancelled") return s;
    // Fallback if your API uses other labels:
    if (/^in\s*progress$/i.test(s)) return "In Progress";
    if (/^complete(d)?$/i.test(s)) return "Completed";
    if (/^cancel(l)?ed$/i.test(s)) return "Cancelled";
    return "New";
}

/** Small helper: is a row receiving started? */
function receivingStarted(init: PolicyInit, row?: RowLike): boolean {
    const id = row?.PurchaseOrderRowId ?? 0;
    if (typeof init.env.hasAnyReceivingStartedForRow === "function") {
        return init.env.hasAnyReceivingStartedForRow(id);
    }
    return (row?.QuantityReceived ?? 0) > 0;
}

/** Parse "Meta.X" -> X */
function parseMetaKey(k: FieldKey): MetaFieldKey | null {
    const m = /^Meta\.(.+)$/.exec(k);
    return (m ? (m[1] as MetaFieldKey) : null);
}

/** Map FieldKey "Row.X" -> ActionKey "Edit_X_Row" for convenience. */
function mapFieldKeyToActionKey(k: FieldKey): ActionKey | null {
    switch (k) {
        case "Row.Quantity": return "Edit_Quantity_Row";
        case "Row.DiscountPct": return "Edit_DiscountPct_Row";
        case "Row.ItemPrice": return "Edit_ItemPrice_Row";
        case "Row.ETA": return "Edit_ETA_Row";
        case "Row.RTA": return "Edit_RTA_Row";
        case "Row.Comment": return "Edit_Comment_Row";
        default: return null;
    }
}

/** Public factory: returns two methods: `field` and `action`. */
export function createPurchaseOrderPolicy(init: PolicyInit) {
    const orderStatus: OrderStatus = statusLabelFromId(init.statusId);

    /* ----------------------------- FIELD API ------------------------------ */
    function field(key: FieldKey, row?: RowLike): boolean {
        // Meta.* are straightforward table lookups
        const meta = parseMetaKey(key);
        if (meta) {
            return !!META_EDIT[orderStatus]?.[meta];
        }

        // Row.* defers to row rules
        const rs = getRowStatus(row);
        const rules = ROW_RULES[orderStatus];

        // Cancelled rows: only Comment is editable (per note (4))
        if (rs === "Cancelled") {
            return key === "Row.Comment";
        }

        const isRestricted = (kind: "fields" | "edit", f?: keyof typeof rules["fields"]) => {
            const flag = f ? rules.fields[f] : rules.edit;
            return flag === "restricted";
        };

        switch (key) {
            case "Row.Quantity":
                if (rules.fields.Quantity === true) return true;
                if (rules.fields.Quantity === false) return false;
                return rs === "New" || rs === "In Progress";
            case "Row.DiscountPct":
                if (rules.fields.DiscountPct === true) return true;
                if (rules.fields.DiscountPct === false) return false;
                return rs === "New" || rs === "In Progress";
            case "Row.ItemPrice":
                if (rules.fields.ItemPrice === true) return true;
                if (rules.fields.ItemPrice === false) return false;
                return rs === "New" || rs === "In Progress";
            case "Row.ETA":
                if (rules.fields.ETA === true) return true;
                if (rules.fields.ETA === false) return false;
                return rs === "New" || rs === "In Progress";
            case "Row.RTA":
                return rules.fields.RTA === true;
            case "Row.Comment":
                if (rules.fields.Comment === true) return true;
                if (rules.fields.Comment === false) return false;
                return rs === "New" || rs === "In Progress";
            default:
                return false;
        }
    }

    /* ----------------------------- ACTION API ----------------------------- */
    function action(key: ActionKey, row?: RowLike): boolean {
        // Order-level transitions
        if (
            key === "Send" ||
            key === "SetConfirmed" ||
            key === "SetShipped" ||
            key === "SetReceivedShort" ||
            key === "Cancel"
        ) {
            const allowedByMatrix = TRANSITIONS[orderStatus].includes(key);
            if (!allowedByMatrix) return false;
            if (key === "Send") {
                const hasActiveRow = (init.rows ?? []).some(r => getRowStatus(r) !== "Cancelled");
                if (!hasActiveRow) return false;
            }
            if (key === "SetReceivedShort" && orderStatus === "In Progress" && init.env.isExternalWarehouse) {
                return false;
            }
            return true;
        }

        // Row-level actions:
        const rs = getRowStatus(row);
        const rules = ROW_RULES[orderStatus];

        if (key === "Add_Row") {
            if (rules.add === true) return true;
            if (rules.add === false) return false;
            return !init.env.isExternalWarehouse; // "ifNotExternalWarehouse"
        }

        if (key === "Delete_Row") {
            if (rules.cancelRow === true) return true;
            if (rules.cancelRow === false) return false;
            // "ifReceivingNotStarted"
            return !receivingStarted(init, row);
        }

        if (key === "Edit_Row") {
            if (rules.edit === true) return rs !== "Completed" && rs !== "Cancelled";
            if (rules.edit === false) return false;
            // "restricted"
            return rs === "New" || rs === "In Progress";
        }


        // Field-specific edit actions (Edit_Quantity_Row, etc.)
        const mapped = mapFieldKeyToActionKey(
            (key.replace(/^Edit_/, "Edit_") as unknown) as FieldKey
        );
        if (mapped) {
            // Delegate to field() so field/action remain consistent
            switch (key) {
                case "Edit_Quantity_Row": return field("Row.Quantity", row);
                case "Edit_DiscountPct_Row": return field("Row.DiscountPct", row);
                case "Edit_ItemPrice_Row": return field("Row.ItemPrice", row);
                case "Edit_ETA_Row": return field("Row.ETA", row);
                case "Edit_RTA_Row": return field("Row.RTA", row);
                case "Edit_Comment_Row": return field("Row.Comment", row);
            }
        }

        return false;
    }

    return { field, action, status: orderStatus };
}
